import React, { useState, useEffect, useMemo } from 'react';
import {
  UserIcon,
  EnvelopeIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { securityService, Permission, User } from '../services/securityService';
import { useAuth } from '../contexts/AuthContext';

function roleDisplayName(u: User | null): string {
  if (!u) return 'No asignado';
  const r = u.role ?? (u as any).Role;
  if (!r) return 'No asignado';
  const desc = (r.description ?? (r as any).Description ?? '').trim();
  const name = (r.name ?? (r as any).Name ?? '').trim();
  return desc || name || 'No asignado';
}

const ProfilePage: React.FC = () => {
  const { user: authUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [centros, setCentros] = useState<any[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [permisos, setPermisos] = useState<Permission[]>([]);
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    mail: '',
    alias: '',
    rolId: null as number | null,
    activo: true,
  });

  const user = useMemo((): User | null => {
    if (authUser) return authUser;
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return securityService.parseUserFromApi(parsed) ?? parsed;
    } catch {
      return null;
    }
  }, [authUser]);

  const lastLogin = localStorage.getItem('lastLogin') || new Date().toLocaleString();

  useEffect(() => {
    const uid = user?.id;
    if (!uid) {
      setLoading(false);
      return;
    }

    const loadUserData = async () => {
      try {
        setLoading(true);
        const [centrosData, assignedClients, myPerms] = await Promise.all([
          securityService.getUsuarioCentros(uid),
          securityService.getUserAssignedClients(uid),
          securityService.getMyPermissions(),
        ]);
        setCentros(centrosData || []);
        setClientes(assignedClients || []);
        setPermisos(Array.isArray(myPerms) ? myPerms : []);
      } catch (error) {
        console.error('Error cargando datos del usuario:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user?.id]);

  const handleEdit = () => {
    if (!user) return;
    setFormData({
      nombre: user.nombre || '',
      apellido: user.apellido || '',
      mail: user.mail || '',
      alias: user.alias || '',
      rolId: user.roleId ?? null,
      activo: user.activo !== undefined ? user.activo : (user.isActive ?? true),
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const activoUsuario = user?.activo ?? user?.isActive ?? true;

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-gray-600 dark:text-gray-400">
        No hay sesión de usuario. Iniciá sesión para ver tu perfil.
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Mi Perfil</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Gestiona tu información personal y preferencias
            </p>
          </div>
          {!isEditing && (
            <button
              onClick={handleEdit}
              className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 dark:bg-primary-500 dark:hover:bg-primary-600"
            >
              <PencilIcon className="w-4 h-4 mr-2" />
              Editar Perfil
            </button>
          )}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-2"
        >
          <div className="!bg-white dark:!bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mr-4">
                <UserIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Información Personal
                </h2>
                <p className="text-gray-600 dark:text-gray-400">Detalles de tu cuenta</p>
              </div>
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nombre
                    </label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      placeholder="Ingrese su nombre"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Apellido
                    </label>
                    <input
                      type="text"
                      value={formData.apellido}
                      onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      placeholder="Ingrese su apellido"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Usuario (Alias)
                    </label>
                    <input
                      type="text"
                      value={formData.alias}
                      onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      placeholder="Ingrese su usuario"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.mail}
                      onChange={(e) => setFormData({ ...formData, mail: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      placeholder="usuario@incer.com"
                    />
                  </div>
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={handleSave}
                    className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200 dark:bg-green-500 dark:hover:bg-green-600"
                  >
                    <CheckIcon className="w-4 h-4 mr-2" />
                    Guardar
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 dark:bg-gray-500 dark:hover:bg-gray-600"
                  >
                    <XMarkIcon className="w-4 h-4 mr-2" />
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center py-3 border-b border-gray-200 dark:border-gray-700">
                  <UserIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Nombre Completo</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {user.nombreCompleto ||
                        `${user.apellido || ''} ${user.nombre || ''}`.trim() ||
                        'No especificado'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center py-3 border-b border-gray-200 dark:border-gray-700">
                  <UserIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Usuario (Alias)</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {user.alias || 'No especificado'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center py-3 border-b border-gray-200 dark:border-gray-700">
                  <EnvelopeIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {user.mail || 'No especificado'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center py-3 border-b border-gray-200 dark:border-gray-700">
                  <UserGroupIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Rol</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {roleDisplayName(user)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center py-3 border-b border-gray-200 dark:border-gray-700">
                  <ShieldCheckIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Estado</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          activoUsuario
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}
                      >
                        {activoUsuario ? 'Activo' : 'Inactivo'}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center py-3">
                  <CalendarIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Último Acceso</p>
                    <p className="font-medium text-gray-900 dark:text-white">{lastLogin}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-6"
        >
          <div className="!bg-white dark:!bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <BuildingOfficeIcon className="w-5 h-5 text-primary-500 dark:text-primary-400 mr-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Centros asignados</h3>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {centros.length} {centros.length === 1 ? 'centro' : 'centros'}
              </span>
            </div>
            <div className="space-y-2">
              {loading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Cargando...</p>
              ) : centros.length > 0 ? (
                centros.map((centro: any, index: number) => (
                  <div
                    key={centro.id || index}
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-primary-500 dark:bg-primary-400 rounded-full mr-3" />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {centro.nombre || `Centro ${index + 1}`}
                        </span>
                        {centro.descripcion && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {centro.descripcion}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No hay centros asignados</p>
              )}
            </div>
          </div>

          <div className="!bg-white dark:!bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <UserGroupIcon className="w-5 h-5 text-primary-500 dark:text-primary-400 mr-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Clientes asignados</h3>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {clientes.length} {clientes.length === 1 ? 'cliente' : 'clientes'}
              </span>
            </div>
            <div className="space-y-2">
              {loading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Cargando...</p>
              ) : clientes.length > 0 ? (
                <>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Clientes a los que tenés acceso asignado explícitamente en la aplicación.
                  </p>
                  {clientes.slice(0, 10).map((cliente: any, index: number) => (
                    <div
                      key={cliente.id || index}
                      className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full mr-3 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block truncate">
                            {cliente.nombre || `Cliente ${index + 1}`}
                          </span>
                          {cliente.centro?.nombre && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {cliente.centro.nombre}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {clientes.length > 10 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center pt-2">
                      +{clientes.length - 10} clientes más
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No tenés clientes asignados directamente
                </p>
              )}
            </div>
          </div>

          <div className="!bg-white dark:!bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center mb-4">
              <ShieldCheckIcon className="w-5 h-5 text-primary-500 dark:text-primary-400 mr-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Permisos del rol</h3>
            </div>
            <div className="space-y-2">
              {loading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Cargando...</p>
              ) : permisos.length > 0 ? (
                <>
                  {permisos.slice(0, 8).map((permiso) => (
                    <div
                      key={permiso.id}
                      className="flex flex-col p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {permiso.name || permiso.description || `Permiso ${permiso.id}`}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {permiso.resource}:{permiso.action}
                        {permiso.scope ? ` · ${permiso.scope}` : ''}
                      </span>
                    </div>
                  ))}
                  {permisos.length > 8 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      +{permisos.length - 8} permisos más
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No hay permisos para mostrar
                </p>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
